import { useAtomValue } from 'jotai';
import { Layer, Source } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import { typeCurveWellsVisibilityAtom } from './atoms';
import {
  tcUwiColorMapAtom,
  tcFilteredUwisAtom,
} from './type-curve-filters/atoms';
import React, { useMemo } from 'react';
import { chunkArray, tileFingerprint } from './utils';

const UWI_CHUNK_SIZE = 500;

interface ScopedWellsLayerProps {
  typeCurveHeader: {
    geometry: string | null;
    createdAt: string;
    developmentType: string | null;
    analogWells: string[];
  };
}

function buildColorExpr(
  uwiColorMap: Record<string, string>,
  defaultColor: string
): string | Expression {
  const entries = Object.entries(uwiColorMap);
  if (entries.length === 0) return defaultColor;

  // Group UWIs by color to produce a compact match expression.
  // Instead of one match case per UWI, we get one case per unique color
  // (typically 3), using array labels for each color group.
  const colorGroups = new Map<string, string[]>();
  for (const [uwi, color] of entries) {
    const group = colorGroups.get(color);
    if (group) {
      group.push(uwi);
    } else {
      colorGroups.set(color, [uwi]);
    }
  }

  const flat: (string | string[])[] = [];
  for (const [color, uwis] of colorGroups) {
    flat.push(uwis.length === 1 ? uwis[0] : uwis, color);
  }

  return [
    'match',
    ['get', 'uwi'],
    ...flat,
    defaultColor,
  ] as unknown as Expression;
}

const ScopedWellsLayer = ({ typeCurveHeader }: ScopedWellsLayerProps) => {
  // One toggle covers all three categories: the group exposes "Type Curve
  // Wells" as a single child, not a parent with sub-toggles.
  const isTypeCurveWellsVisible = useAtomValue(typeCurveWellsVisibilityAtom);
  const uwiColorMap = useAtomValue(tcUwiColorMapAtom);
  const filteredUwis = useAtomValue(tcFilteredUwisAtom);

  const filteredUwiStrings = useMemo(
    () => filteredUwis.map((w) => w.uwi),
    [filteredUwis]
  );

  const newWellsTiles = useMemo(() => {
    if (!filteredUwiStrings || filteredUwiStrings.length === 0) {
      return [];
    }

    const baseUrl = `${window.location.origin}/queries/wells/mvt`;

    return chunkArray(filteredUwiStrings, UWI_CHUNK_SIZE).map((chunk) => {
      const params = new URLSearchParams();
      params.set('uwis', chunk.join(','));
      return `${baseUrl}/survey/{z}/{x}/{y}.mvt?${params.toString()}`;
    });
  }, [filteredUwiStrings]);

  const wellsInAreaTiles = useMemo(() => {
    if (!filteredUwiStrings || filteredUwiStrings.length === 0) {
      return [];
    }

    const baseUrl = `${window.location.origin}/queries/wells/mvt`;

    return chunkArray(filteredUwiStrings, UWI_CHUNK_SIZE).map((chunk) => {
      const params = new URLSearchParams();
      params.set('uwis', chunk.join(','));
      return `${baseUrl}/survey/{z}/{x}/{y}.mvt?${params.toString()}`;
    });
  }, [filteredUwiStrings]);

  const wellControlTiles = useMemo(() => {
    if (!filteredUwiStrings || filteredUwiStrings.length === 0) {
      return [];
    }

    const baseUrl = `${window.location.origin}/queries/wells/mvt`;

    let controlUwis = typeCurveHeader.analogWells ?? [];

    if (filteredUwiStrings && filteredUwiStrings.length > 0) {
      const visibleControlUwis = controlUwis.filter((uwi) =>
        filteredUwiStrings.includes(uwi)
      );
      if (visibleControlUwis.length > 0) {
        controlUwis = visibleControlUwis;
      } else {
        return [];
      }
    }

    return chunkArray(controlUwis, UWI_CHUNK_SIZE).map((chunk) => {
      const params = new URLSearchParams();
      params.set('uwis', chunk.join(','));
      return `${baseUrl}/survey/{z}/{x}/{y}.mvt?${params.toString()}`;
    });
  }, [typeCurveHeader, filteredUwiStrings]);

  // Base color for all wells is blue; well qualifier rule and UWI-specific
  // color-by rules override via the uwiColorMap match expression.
  const DEFAULT_WELL_COLOR = '#0F62FE';

  const newWellsColor = useMemo(
    () => buildColorExpr(uwiColorMap, DEFAULT_WELL_COLOR),
    [uwiColorMap]
  );
  const wellsInAreaColor = useMemo(
    () => buildColorExpr(uwiColorMap, DEFAULT_WELL_COLOR),
    [uwiColorMap]
  );
  const wellControlColor = useMemo(
    () => buildColorExpr(uwiColorMap, DEFAULT_WELL_COLOR),
    [uwiColorMap]
  );

  const newWellsFp = useMemo(
    () => tileFingerprint(newWellsTiles),
    [newWellsTiles]
  );
  const wellsInAreaFp = useMemo(
    () => tileFingerprint(wellsInAreaTiles),
    [wellsInAreaTiles]
  );
  const wellControlFp = useMemo(
    () => tileFingerprint(wellControlTiles),
    [wellControlTiles]
  );

  return (
    <>
      {/* New Wells Layers */}
      {isTypeCurveWellsVisible &&
        newWellsTiles.map((tileUrl, idx) => (
          <React.Fragment key={`new-wells-${newWellsFp}-${idx}`}>
            <Source
              id={`new-wells-survey-${newWellsFp}-${idx}`}
              type="vector"
              tiles={[tileUrl]}
            >
              <Layer
                id={`type-curve-scoped-wells-new-survey-lines-${newWellsFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'line-color': newWellsColor,
                  'line-width': 4,
                  'line-opacity': 0.8,
                }}
              />
            </Source>

            <Source
              id={`new-wells-stick-${newWellsFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/stick/')]}
            >
              <Layer
                id={`type-curve-scoped-wells-new-stick-lines-${newWellsFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all', ['!', ['has', 'survey']]]}
                paint={{
                  'line-color': newWellsColor,
                  'line-width': 4,
                  'line-opacity': 0.6,
                  'line-dasharray': [1, 1],
                }}
              />
            </Source>

            <Source
              id={`new-wells-surface-hole-${newWellsFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/surface_hole/')]}
            >
              <Layer
                id={`new-wells-surface-hole-points-${newWellsFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': newWellsColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.8,
                }}
              />
            </Source>

            <Source
              id={`new-wells-bottom-hole-${newWellsFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/bottom_hole/')]}
            >
              <Layer
                id={`new-wells-bottom-hole-points-${newWellsFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': newWellsColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.8,
                }}
              />
            </Source>
          </React.Fragment>
        ))}

      {/* Wells in Type Curve Area Layer */}
      {isTypeCurveWellsVisible &&
        wellsInAreaTiles.map((tileUrl, idx) => (
          <React.Fragment key={`wells-in-area-${wellsInAreaFp}-${idx}`}>
            <Source
              id={`wells-in-area-survey-${wellsInAreaFp}-${idx}`}
              type="vector"
              tiles={[tileUrl]}
            >
              <Layer
                id={`type-curve-scoped-wells-in-area-survey-lines-${wellsInAreaFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'line-color': wellsInAreaColor,
                  'line-width': 4,
                  'line-opacity': 0.8,
                }}
              />
            </Source>

            <Source
              id={`wells-in-area-stick-${wellsInAreaFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/stick/')]}
            >
              <Layer
                id={`type-curve-scoped-wells-in-area-stick-lines-${wellsInAreaFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all', ['!', ['has', 'survey']]]}
                paint={{
                  'line-color': wellsInAreaColor,
                  'line-width': 4,
                  'line-opacity': 0.6,
                  'line-dasharray': [1, 1],
                }}
              />
            </Source>

            <Source
              id={`wells-in-area-surface-hole-${wellsInAreaFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/surface_hole/')]}
            >
              <Layer
                id={`wells-in-area-surface-hole-points-${wellsInAreaFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': wellsInAreaColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.8,
                }}
              />
            </Source>

            <Source
              id={`wells-in-area-bottom-hole-${wellsInAreaFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/bottom_hole/')]}
            >
              <Layer
                id={`wells-in-area-bottom-hole-points-${wellsInAreaFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': wellsInAreaColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.8,
                }}
              />
            </Source>
          </React.Fragment>
        ))}

      {/* Well Control Layer */}
      {isTypeCurveWellsVisible &&
        typeCurveHeader.analogWells &&
        typeCurveHeader.analogWells.length > 0 &&
        wellControlTiles.map((tileUrl, idx) => (
          <React.Fragment key={`well-control-${wellControlFp}-${idx}`}>
            <Source
              id={`well-control-survey-${wellControlFp}-${idx}`}
              type="vector"
              tiles={[tileUrl]}
            >
              <Layer
                id={`type-curve-scoped-wells-control-survey-lines-${wellControlFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'line-color': wellControlColor,
                  'line-width': 4,
                  'line-opacity': 0.9,
                }}
              />
            </Source>

            <Source
              id={`well-control-stick-${wellControlFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/stick/')]}
            >
              <Layer
                id={`type-curve-scoped-wells-control-stick-lines-${wellControlFp}-${idx}`}
                type="line"
                source-layer="hits"
                filter={['all', ['!', ['has', 'survey']]]}
                paint={{
                  'line-color': wellControlColor,
                  'line-width': 4,
                  'line-opacity': 0.7,
                  'line-dasharray': [1, 1],
                }}
              />
            </Source>

            <Source
              id={`well-control-surface-hole-${wellControlFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/surface_hole/')]}
            >
              <Layer
                id={`well-control-surface-hole-points-${wellControlFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': wellControlColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.9,
                }}
              />
            </Source>

            <Source
              id={`well-control-bottom-hole-${wellControlFp}-${idx}`}
              type="vector"
              tiles={[tileUrl.replace('/survey/', '/bottom_hole/')]}
            >
              <Layer
                id={`well-control-bottom-hole-points-${wellControlFp}-${idx}`}
                type="circle"
                source-layer="hits"
                filter={['all']}
                paint={{
                  'circle-color': wellControlColor,
                  'circle-radius': 4,
                  'circle-opacity': 0.9,
                }}
              />
            </Source>
          </React.Fragment>
        ))}
    </>
  );
};

export { ScopedWellsLayer };
